import React, { useState, useMemo, useEffect } from 'react';
import { AlertTriangle, CheckCircle, XCircle, ExternalLink, Maximize2, X } from 'lucide-react';
import './FailureReportRenderer.css';

interface TaskSummary {
  task_alias: string;
  task_uuid?: string;
  project_uuid?: string;
  project_name?: string;
  failure_count: number;
  latest_failure_time?: string;
  current_status: 'STILL BROKEN' | 'RECOVERED';
  latest_status: string;
  ui_link?: string;
  error_preview?: string;
}

interface FailureReportData {
  type: string;
  title: string;
  summary: {
    total_failures: number;
    tasks_still_broken: number;
    tasks_recovered: number;
    date_range?: {
      from: string;
      to: string;
      days_back: number;
    };
  };
  critical: TaskSummary[];
  warning: TaskSummary[];
}

interface FailureReportRendererProps {
  reportData: FailureReportData;
}

const FailureReportRenderer: React.FC<FailureReportRendererProps> = ({ reportData }) => {
  // Filter state - 'all' | 'critical' | 'recovered'
  const [filterBy, setFilterBy] = useState<'all' | 'critical' | 'recovered'>('all');

  // Sort state - 'count' (default) | 'date'
  const [sortBy, setSortBy] = useState<'count' | 'date'>('count');

  // Project filter state
  const [selectedProject, setSelectedProject] = useState<string>('all');

  // Expanded/fullscreen state
  const [isExpanded, setIsExpanded] = useState(false);

  // Handle Escape key to close expanded view
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isExpanded]);

  // Lock body scroll when expanded
  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isExpanded]);

  // Extract data from reportData (with safe fallbacks for hooks)
  const title = reportData?.title;
  const summary = reportData?.summary;
  const critical = reportData?.critical || [];
  const warning = reportData?.warning || [];

  // Extract unique projects from data
  const projects = useMemo(() => {
    const allTasks = [...critical, ...warning];
    const uniqueProjects = new Map<string, string>();
    allTasks.forEach(task => {
      if (task.project_uuid) {
        // Use project_name if available, otherwise use project_uuid as display name
        const displayName = task.project_name || task.project_uuid.substring(0, 8) + '...';
        uniqueProjects.set(task.project_uuid, displayName);
      }
    });
    return Array.from(uniqueProjects.entries());
  }, [critical, warning]);

  // Apply filters and sorting to critical tasks
  const filteredCritical = useMemo(() => {
    let tasks = critical;

    // Project filter
    if (selectedProject !== 'all') {
      tasks = tasks.filter(t => t.project_uuid === selectedProject);
    }

    // Sort
    if (sortBy === 'date') {
      tasks = [...tasks].sort((a, b) =>
        new Date(b.latest_failure_time || 0).getTime() -
        new Date(a.latest_failure_time || 0).getTime()
      );
    }
    // Default sort by count is already applied by backend

    return tasks;
  }, [critical, selectedProject, sortBy]);

  // Apply filters and sorting to warning tasks
  const filteredWarning = useMemo(() => {
    let tasks = warning;

    // Project filter
    if (selectedProject !== 'all') {
      tasks = tasks.filter(t => t.project_uuid === selectedProject);
    }

    // Sort
    if (sortBy === 'date') {
      tasks = [...tasks].sort((a, b) =>
        new Date(b.latest_failure_time || 0).getTime() -
        new Date(a.latest_failure_time || 0).getTime()
      );
    }

    return tasks;
  }, [warning, selectedProject, sortBy]);

  // Early return after all hooks
  if (!reportData) {
    return null;
  }

  const formatTime = (isoString?: string) => {
    if (!isoString) return 'Unknown';
    try {
      const date = new Date(isoString);
      return date.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return isoString;
    }
  };

  // Extract URL from markdown link format: [text](url "title")
  const extractLink = (markdownLink?: string): { text: string; url: string } | null => {
    if (!markdownLink) return null;
    const match = markdownLink.match(/\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/);
    if (match) {
      return { text: match[1], url: match[2] };
    }
    return null;
  };

  // Handle keyboard events on clickable stats
  const handleStatKeyDown = (e: React.KeyboardEvent, filter: 'all' | 'critical' | 'recovered') => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setFilterBy(filter);
    }
  };

  const renderTaskCard = (task: TaskSummary, index: number, isCritical: boolean) => {
    const link = extractLink(task.ui_link);

    return (
      <div
        key={`${task.task_alias}-${index}`}
        className={`failure-task-card ${isCritical ? 'critical' : 'warning'}`}
      >
        <div className="task-header">
          <div className="task-status-icon">
            {isCritical ? (
              <XCircle size={18} className="icon-critical" />
            ) : (
              <CheckCircle size={18} className="icon-recovered" />
            )}
          </div>
          <div className="task-info">
            <span className="task-alias">{task.task_alias}</span>
            <span className={`status-badge ${isCritical ? 'badge-critical' : 'badge-recovered'}`}>
              {task.current_status}
            </span>
          </div>
          {link && (
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="task-link"
              title="View in NomnomData"
            >
              <ExternalLink size={14} />
            </a>
          )}
        </div>

        <div className="task-details">
          <div className="detail-row">
            <span className="detail-label">Failures:</span>
            <span className="detail-value failure-count">{task.failure_count}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Last failure:</span>
            <span className="detail-value">{formatTime(task.latest_failure_time)}</span>
          </div>
          {task.latest_status && (
            <div className="detail-row">
              <span className="detail-label">Current state:</span>
              <span className="detail-value">{task.latest_status}</span>
            </div>
          )}
        </div>

        {task.error_preview && (
          <div className="error-preview">
            <code>{task.error_preview}</code>
          </div>
        )}
      </div>
    );
  };

  // Render summary stats (used in both inline and expanded views)
  const renderSummaryStats = () => (
    <div className="report-summary">
      <div
        className={`summary-stat clickable ${filterBy === 'all' ? 'active' : ''}`}
        onClick={() => setFilterBy('all')}
        onKeyDown={(e) => handleStatKeyDown(e, 'all')}
        role="button"
        tabIndex={0}
        aria-pressed={filterBy === 'all'}
        aria-label="Show all failures"
      >
        <span className="stat-value">{summary.total_failures}</span>
        <span className="stat-label">Total Failures</span>
      </div>
      <div
        className={`summary-stat critical clickable ${filterBy === 'critical' ? 'active' : ''}`}
        onClick={() => setFilterBy('critical')}
        onKeyDown={(e) => handleStatKeyDown(e, 'critical')}
        role="button"
        tabIndex={0}
        aria-pressed={filterBy === 'critical'}
        aria-label="Show only critical tasks"
      >
        <span className="stat-value">{summary.tasks_still_broken}</span>
        <span className="stat-label">Tasks Still Broken</span>
      </div>
      <div
        className={`summary-stat recovered clickable ${filterBy === 'recovered' ? 'active' : ''}`}
        onClick={() => setFilterBy('recovered')}
        onKeyDown={(e) => handleStatKeyDown(e, 'recovered')}
        role="button"
        tabIndex={0}
        aria-pressed={filterBy === 'recovered'}
        aria-label="Show only recovered tasks"
      >
        <span className="stat-value">{summary.tasks_recovered}</span>
        <span className="stat-label">Tasks Recovered</span>
      </div>
    </div>
  );

  // Render controls bar (used in both inline and expanded views)
  const renderControls = () => (
    <div className="report-controls">
      {/* Project filter - only show if multiple projects */}
      {projects.length > 1 && (
        <div className="control-group">
          <label htmlFor="project-filter">Project:</label>
          <select
            id="project-filter"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
          >
            <option value="all">All Projects ({projects.length})</option>
            {projects.map(([uuid, name]) => (
              <option key={uuid} value={uuid}>{name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Sort options */}
      <div className="control-group">
        <label>Sort by:</label>
        <div className="sort-buttons">
          <button
            className={sortBy === 'count' ? 'active' : ''}
            onClick={() => setSortBy('count')}
            aria-pressed={sortBy === 'count'}
          >
            Most Failures
          </button>
          <button
            className={sortBy === 'date' ? 'active' : ''}
            onClick={() => setSortBy('date')}
            aria-pressed={sortBy === 'date'}
          >
            Most Recent
          </button>
        </div>
      </div>
    </div>
  );

  // Render task sections (used in both inline and expanded views)
  const renderTaskSections = (isExpandedView: boolean = false) => (
    <div className={isExpandedView ? 'expanded-sections' : ''}>
      {/* Only show critical section if filter is 'all' or 'critical' */}
      {(filterBy === 'all' || filterBy === 'critical') && filteredCritical.length > 0 && (
        <div className="report-section">
          <h4 className="section-title critical-title">
            <XCircle size={16} />
            Critical - Needs Immediate Attention ({filteredCritical.length})
          </h4>
          <div className="task-list">
            {filteredCritical.map((task, index) => renderTaskCard(task, index, true))}
          </div>
        </div>
      )}

      {/* Only show warning section if filter is 'all' or 'recovered' */}
      {(filterBy === 'all' || filterBy === 'recovered') && filteredWarning.length > 0 && (
        <div className="report-section">
          <h4 className="section-title warning-title">
            <CheckCircle size={16} />
            Recovered - For Awareness ({filteredWarning.length})
          </h4>
          <div className="task-list">
            {filteredWarning.map((task, index) => renderTaskCard(task, index, false))}
          </div>
        </div>
      )}

      {/* Show empty state when filter results in no tasks */}
      {filteredCritical.length === 0 && filteredWarning.length === 0 && (
        <div className="no-failures">
          <CheckCircle size={24} className="icon-success" />
          <p>
            {filterBy === 'all'
              ? 'No failed executions found in the specified time range.'
              : filterBy === 'critical'
              ? 'No critical tasks found.'
              : 'No recovered tasks found.'}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Inline view */}
      <div className="failure-report-container">
        <div className="report-header">
          <AlertTriangle size={20} className="report-icon" />
          <h3 className="report-title">{title || 'Failed Executions Report'}</h3>
          <button
            className="expand-button"
            onClick={() => setIsExpanded(true)}
            title="View full report"
            aria-label="Expand report to full screen"
          >
            <Maximize2 size={16} />
          </button>
        </div>

        {renderSummaryStats()}
        {renderControls()}
        {renderTaskSections()}
      </div>

      {/* Expanded modal overlay */}
      {isExpanded && (
        <div
          className="failure-report-overlay"
          onClick={() => setIsExpanded(false)}
          role="presentation"
        >
          <div
            className="failure-report-expanded"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Expanded failure report"
          >
            <div className="expanded-header">
              <AlertTriangle size={24} className="report-icon" />
              <h2 className="expanded-title">{title || 'Failed Executions Report'}</h2>
              <button
                className="close-button"
                onClick={() => setIsExpanded(false)}
                title="Close"
                aria-label="Close expanded view"
              >
                <X size={20} />
              </button>
            </div>

            <div className="expanded-content">
              {renderSummaryStats()}
              {renderControls()}
              {renderTaskSections(true)}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FailureReportRenderer;
